import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import SectionHeading from "@/components/SectionHeading";
import { ArrowRight } from "lucide-react";
import { useContent } from "@/context/ContentContext";
import { imgSrc } from "@/lib/api";
import SEO from "@/components/SEO";

export default function Products() {
  const { products } = useContent();
  const [filter, setFilter] = useState("All");

  const categories = useMemo(() => {
    const set = new Set(["All"]);
    products.forEach((p) => set.add(p.category));
    return Array.from(set);
  }, [products]);

  const filtered = filter === "All" ? products : products.filter((p) => p.category === filter);

  return (
    <div data-testid="products-page" className="pt-32 pb-24">
      <SEO path="/products" title="Aluminium Sections, ACP & Hardware Catalogue"
        description="Buy aluminium sliding window profiles, door profiles, partition systems, City BOND ACP, HPL panels, netlon, modular kitchen frames, ladders, locks and handles from Chennai's premium aluminium dealer." />
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 mb-12">
        <SectionHeading eyebrow="Product Catalogue" title="Aluminium, hardware, and architectural systems — built for every application."
          sub="From sliding profiles for a single villa to ACP and HPL for a 30-storey tower, our catalogue covers the full spectrum of premium aluminium." />
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="flex flex-wrap gap-2 mb-10 border-b border-[#E5E7EB]">
          {categories.map((c) => (
            <button key={c} data-testid={`product-filter-${c.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => setFilter(c)}
              className={`px-4 py-3 text-xs tracking-widest uppercase font-semibold transition-colors border-b-2 -mb-px ${filter === c ? "border-[#D4AF37] text-[#1A1A1A]" : "border-transparent text-[#6B7280] hover:text-[#1A1A1A]"}`}>
              {c}
            </button>
          ))}
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((p) => (
            <article key={p.id} data-testid={`product-card-${p.id}`} className="product-card bg-white border border-[#E5E7EB] overflow-hidden">
              <div className="aspect-[4/3] overflow-hidden bg-[#F3F4F6]">
                <img src={imgSrc(p.image)} alt={p.name} className="product-card-img w-full h-full object-cover" />
              </div>
              <div className="p-6">
                <span className="tracking-eyebrow text-[#D4AF37]">{p.category}</span>
                <h3 className="font-display text-xl mt-3 leading-snug">{p.name}</h3>
                <p className="text-sm text-[#4B5563] mt-2 leading-relaxed">{p.description}</p>
                <ul className="mt-4 flex flex-wrap gap-2">
                  {(p.features || []).map((f) => (
                    <li key={f} className="text-[11px] tracking-wider uppercase px-2.5 py-1 bg-[#FAFAFA] border border-[#E5E7EB] text-[#4B5563]">{f}</li>
                  ))}
                </ul>
                <Link to="/contact" data-testid={`product-enquire-${p.id}`} className="mt-6 inline-flex items-center gap-2 text-xs tracking-widest uppercase font-semibold border-b-2 border-[#D4AF37] pb-1">
                  Enquire <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-24 grid md:grid-cols-4 gap-px bg-[#E5E7EB] border border-[#E5E7EB]">
          {["Residential Projects", "Commercial Buildings", "Interior Solutions", "Industrial Applications"].map((a) => (
            <div key={a} className="bg-white p-7">
              <div className="font-display text-lg">{a}</div>
              <div className="tracking-eyebrow text-[#D4AF37] mt-2">Application</div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
